import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useBrand } from './brand';
import { ordinal, spPlace } from './lib/format';
import './primitives.css';

/** Finishing position pill: "✓ 1st · $5.00", "2nd · $2.30", or a pending start time. */
export function ResultChip({ pos, sp, place, pending }: { pos?: number | null; sp?: number | null; place?: number | null; pending?: string }) {
  if (pending) return <span className="ui-res pending">Runs {pending}</span>;
  if (pos == null || pos <= 0 || pos === 999) return null;
  const cls = pos === 1 ? 'won' : pos === 2 ? 'second' : pos === 3 ? 'third' : 'out';
  return (
    <span className={`ui-res ${cls}`}>
      {pos === 1 ? '✓ ' : ''}
      {ordinal(pos)}
      {sp ? ` · ${spPlace(sp, place)}` : ''}
    </span>
  );
}

/** The solid padlock the site has always used on locked meetings. */
export function LockIcon() {
  return (
    <svg className="ui-lock" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
    </svg>
  );
}

export function ScratchedTag() {
  return <span className="ui-scrtag">Scratched</span>;
}

type Going = 'good' | 'soft' | 'heavy' | 'synthetic';

/** Groups a rating the way the site always has: Good/Firm/Fast, Soft, Heavy, everything else. */
export function goingGroup(condition: string | null | undefined): Going {
  const c = (condition ?? '').toLowerCase().split(' ')[0];
  if (c === 'soft') return 'soft';
  if (c === 'heavy') return 'heavy';
  if (c === 'good' || c === 'firm' || c === 'fast') return 'good';
  return 'synthetic';
}

const GOING_ICON: Record<Going, ReactNode> = {
  good: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  soft: <path d="M7 18h10a4 4 0 0 0 0-8 6 6 0 0 0-11.5 1.5A3.5 3.5 0 0 0 7 18z" />,
  heavy: (
    <>
      <path d="M7 14h10a4 4 0 0 0 0-8 6 6 0 0 0-11.5 1.5A3.5 3.5 0 0 0 7 14z" />
      <path d="M8 18l-1 2M12 18l-1 2M16 18l-1 2" />
    </>
  ),
  synthetic: (
    <>
      <path d="M3 19h18" />
      <path d="M4 15h16M4 11h16M8 8v11M12 8v11M16 8v11" />
    </>
  ),
};

/** Track rating badge with an icon ("☀ GOOD 4"). Unknown ratings read "TBC". */
export function GoingBadge({ condition, iconOnly = false }: { condition: string | null | undefined; iconOnly?: boolean }) {
  const group = goingGroup(condition);
  const first = (condition ?? '').toLowerCase().split(' ')[0];
  const label = !condition || first === 'unknown' ? 'TBC' : condition;
  // iconOnly: the coloured icon alone, for tight rows; nothing when the going isn't known
  if (iconOnly) {
    return label === 'TBC' ? null : (
      <span className={`ui-gb icon ${group}`} role="img" aria-label={label} title={label}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {GOING_ICON[group]}
        </svg>
      </span>
    );
  }
  return (
    <span className={`ui-gb ${group}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {GOING_ICON[group]}
      </svg>
      {label}
    </span>
  );
}

/** The brand's agreement pill ("★ Prime Pick"). iconOnly shows just the star, with the name as its label. */
export function Mark({ show = true, iconOnly = false }: { show?: boolean; iconOnly?: boolean }) {
  const { markName } = useBrand();
  if (!show) return null;
  return iconOnly ? (
    <span className="ui-mark icon" role="img" aria-label={markName} title={markName} />
  ) : (
    <span className="ui-mark">{markName}</span>
  );
}

/** Our chance of winning as a ring. The colour comes from the nearest --ui-ring. */
export function ChanceRing({ pct, label = 'Chance' }: { pct: number | null | undefined; label?: string }) {
  const known = pct != null && !Number.isNaN(Number(pct));
  const value = known ? Math.max(0, Math.min(100, Number(pct))) : 0;
  return (
    <span
      className="ui-ring"
      style={{ ['--pct' as string]: value }}
      role="img"
      aria-label={known ? `${label} ${Math.round(value)} percent` : `${label} unknown`}
    >
      <span>
        {known ? `${Math.round(value)}%` : '–'}
        <small>{label.toUpperCase()}</small>
      </span>
    </span>
  );
}

export interface SegOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Full name when the label is a short one: shown on hover and read by screen readers. */
  title?: string;
}

/** A two-or-three-way switch ("Recent | All", "Cards | Compact"). */
export function Seg<T extends string>({
  options,
  value,
  onChange,
  label,
  tone = 'light',
}: {
  options: SegOption<T>[];
  value: T;
  onChange: (v: T) => void;
  label: string;
  /** 'dark' for use on a dark panel. */
  tone?: 'light' | 'dark';
}) {
  return (
    <div className={`ui-seg ${tone}`} role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} title={o.title} aria-label={o.title} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** An ⓘ that explains a figure: tap (or hover) to read it; tap away or Escape closes it. */
export function Help({ text, title }: { text: string; title: string }) {
  // hover shows it; a tap pins it (a mouse click lands mid-hover, so a plain toggle would close it again)
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hover || pinned;
  const close = () => {
    setHover(false);
    setPinned(false);
  };
  const box = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !box.current?.contains(e.target as Node) && close();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <span className="ui-help" ref={box} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button type="button" aria-label={`About ${title}`} aria-expanded={open} onClick={() => (pinned ? close() : setPinned(true))}>
        ⓘ
      </button>
      {open && (
        <span className="tip" role="tooltip">
          {text}
        </span>
      )}
    </span>
  );
}
